import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';

/**
 * Landing page for the Microsoft SSO redirect. The server places the
 * access + refresh tokens in the URL fragment (`#access=…&refresh=…`)
 * so they never appear in browser history or server logs. The page:
 *   1. Reads them out of `window.location.hash`.
 *   2. Clears the hash so a back-navigation doesn't re-leak them to
 *      anything that snapshots the URL.
 *   3. Fetches /api/users/profile with the new access token to hydrate
 *      AuthContext (same shape as the password-login flow).
 *   4. Calls setAuthData and redirects to /dashboard.
 *
 * If anything fails the user lands back on /login with an error param.
 */
const SSOCallback: React.FC = () => {
  const navigate = useNavigate();
  const { setAuthData } = useAuth();
  const ranRef = useRef(false);

  useEffect(() => {
    // Strict-mode dev environments run effects twice. The second pass
    // would re-consume tokens from the hash (which we've already
    // cleared by then) and bounce the user to the error page.
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      try {
        const hash = window.location.hash.replace(/^#/, '');
        const params = new URLSearchParams(hash);
        const access = params.get('access');
        const refresh = params.get('refresh');
        if (!access) {
          navigate('/login?error=missing_token', { replace: true });
          return;
        }

        // Clear the fragment immediately so the tokens don't sit in
        // window.location for the rest of the SPA's lifetime.
        window.history.replaceState(null, '', '/sso-callback');

        // Load the user profile so AuthContext gets a real user.
        const res = await axios.get(`${API_URL}/api/users/profile`, {
          headers: { Authorization: `Bearer ${access}` },
        });
        const user = res.data;
        setAuthData(access, user, refresh || undefined);
        navigate('/dashboard', { replace: true });
      } catch (err) {
        navigate('/login?error=sso_finalize_failed', { replace: true });
      }
    })();
  }, [navigate, setAuthData]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Arial, sans-serif',
      color: '#475569',
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 48, height: 48,
          border: '4px solid #e2e8f0',
          borderTopColor: '#667eea',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          margin: '0 auto 16px',
        }} />
        <p>מתחבר...</p>
        <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
      </div>
    </div>
  );
};

export default SSOCallback;
